import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "";

export default function ResetPassword() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const token          = params.get("token");

  const [form, setForm]       = useState({ password: "", confirm: "" });
  const [show, setShow]       = useState({ password: false, confirm: false });
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm)
      return setError("Passwords do not match.");
    if (form.password.length < 8)
      return setError("Password must be at least 8 characters.");

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed.");
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: "var(--t-surface-mid)",
    border: "1px solid #334155",
    borderRadius: 8,
    padding: "10px 36px",
    color: "var(--t-text-primary)",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  };

  const iconStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#475569",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--t-page)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 400 }}>

        {/* Logo / Brand */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img
            src="/logo.jpg"
            alt="MIDTC"
            style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", margin: "0 auto 16px", display: "block", border: "3px solid var(--t-border-mid)" }}
            onError={e => { e.target.style.display = "none"; }}
          />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--t-text-primary)", margin: 0 }}>MIDTC</h1>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>Support Portal</p>
        </div>

        {/* Card */}
        <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 16, padding: 32 }}>
          {success ? (
            /* ── Success state ── */
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "#064e3b",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <CheckCircle size={28} color="#34d399" />
              </div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 8 }}>Password updated!</h2>
              <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>Redirecting you to sign in...</p>
            </div>
          ) : (
            /* ── Form state ── */
            <>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--t-text-body)", margin: "0 0 6px" }}>Set a new password</h2>
              <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 24px" }}>
                Choose a strong password of at least 8 characters.
              </p>

              {error && (
                <div style={{
                  background: "#7f1d1d", border: "1px solid #dc2626",
                  borderRadius: 8, padding: "10px 14px", marginBottom: 20,
                  display: "flex", alignItems: "center", gap: 8,
                  color: "#fca5a5", fontSize: 13,
                }}>
                  <AlertCircle size={15} /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { label: "New Password",     field: "password" },
                  { label: "Confirm Password", field: "confirm"  },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, display: "block", marginBottom: 6 }}>
                      {label}
                    </label>
                    <div style={{ position: "relative" }}>
                      {/* Left lock icon */}
                      <span style={{ ...iconStyle, left: 12 }}>
                        <Lock size={14} />
                      </span>
                      <input
                        type={show[field] ? "text" : "password"}
                        value={form[field]}
                        onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        style={inputStyle}
                      />
                      {/* Right toggle icon */}
                      <button
                        type="button"
                        onClick={() => setShow(p => ({ ...p, [field]: !p[field] }))}
                        style={{ ...iconStyle, right: 12, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      >
                        {show[field] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: loading ? "#1e3a5f" : "#4f6fff",
                    color: "#fff",
                    padding: "11px",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 14,
                    marginTop: 8,
                    opacity: loading ? 0.7 : 1,
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    width: "100%",
                  }}
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  style={{ background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", marginTop: 4 }}
                >
                  ← Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
